// Compat imports (not the modular v9+ API) - these preserve the exact same
// namespaced surface (firebase.auth(), firebase.database(), provider
// constructors, etc.) this file already used under firebase 7.14.2/8.x, so
// nothing below this import block needed to change. Chosen over the plain
// (non-compat) 'firebase/app' etc. specifically so this package can share
// the same firebase version as packages/functions' devDependency (also
// 12.15.0, used for the rules-unit-testing suite) without two different
// @firebase/app-family versions coexisting in the pnpm workspace - see
// AGENTS.md / the "bump packages/web firebase to 12.15.0" commit message
// for the exact bug that caused (a bare, undeclared
// `require('@firebase/app'[-compat])` inside @firebase/database[-compat]
// resolving to the wrong version when more than one was present; formerly
// worked around via a now-removed pnpm-workspace.yaml packageExtensions
// entry, no longer needed once there was only one @firebase/app version).
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/database';
import 'firebase/compat/performance';
import type { Store, UnknownAction } from 'redux';
import FIREBASE_CONFIG from './config';

type EmailPasswordUser = {
  email: string;
  password: string;
};

type ProfileUpdate = {
  displayName?: string | null;
  photoURL?: string | null;
};

type AuthProviderName =
  'email' | 'facebook' | 'github' | 'google' | 'twitter' | string;

type ErrorResult = { error: unknown };
type UserResult = { user: firebase.User | null };
type LogoutResult = { success: number; message: string };
type MessageResult = { message: string };

type EmulatorPerfTrace = {
  start: () => void;
  stop: () => void;
  record: () => void;
  putMetric: () => void;
  getMetric: () => number;
  incrementMetric: () => void;
  putAttribute: () => void;
  getAttribute: () => undefined;
  removeAttribute: () => void;
  getAttributes: () => Record<string, never>;
};

type EmulatorPerf = {
  trace: () => EmulatorPerfTrace;
};

declare global {
  interface Window {
    __myfolioE2E__?: {
      signInAsExistingUser: (
        email: string,
        password: string
      ) => Promise<firebase.auth.UserCredential>;
    };
  }
}

// Routes Auth/RTDB traffic to the local Firebase Emulator Suite instead of
// a real project - for the Playwright e2e suite only. (e2e/support/
// firebaseAdmin.ts's getEmulatorAdminApp() is this file's Admin-SDK
// counterpart - same emulator, its own env-var-based guardrail instead of
// this file's `demo-` check, since the Admin SDK routes to emulators via
// FIREBASE_AUTH_EMULATOR_HOST/FIREBASE_DATABASE_EMULATOR_HOST rather than
// an explicit useEmulator() call.) THREE independent
// conditions must ALL hold before one byte of traffic is redirected - any
// single one alone keeps a real dev/stage/prod bundle on real Firebase:
//  1. process.env.APP_ENV === 'emulator' is a BUILD-time constant, folded
//     to a string literal by Vite's define (vite.config.ts).
//     build:dev/build:stage/build:prod (root package.json) pass appEnv=
//     development/stage/production respectively - none can ever produce
//     'emulator', so esbuild's minifier deletes this whole block from those bundles.
//  2. The resolved config's projectId must start with `demo-` - even a
//     hypothetically leaked emulator bundle then holds no credential for,
//     and cannot reach, my-portfolio-{test,stage,prod}.
//  3. The page must be on a loopback host - an emulator is by definition
//     local, so a deployed bundle satisfying #1 and #2 (which can't happen
//     per #1, but belt-and-braces) is still inert on a real domain.
const USE_EMULATORS =
  process.env.APP_ENV === 'emulator' &&
  FIREBASE_CONFIG.projectId.startsWith('demo-') &&
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

export const firebaseApp = firebase.initializeApp(FIREBASE_CONFIG);

export const firebaseAuth = firebase.auth();
if (USE_EMULATORS) {
  // Per this SDK's own typings: must be called synchronously, immediately
  // after the first call to firebase.auth() - hence right here, not in any
  // deferred/async init path. 127.0.0.1 (not `localhost`) matches current
  // Firebase docs' own recommendation, avoiding a `localhost` -> `::1`
  // resolution mismatch against an emulator bound to IPv4.
  // Compat Auth typings only declare the URL arg; runtime accepts the options
  // bag (disableWarnings) — same call as the previous .js source.
  (
    firebaseAuth.useEmulator as (
      url: string,
      options?: { disableWarnings?: boolean }
    ) => void
  )('http://127.0.0.1:9099', { disableWarnings: true });
}

export const firebaseDB = firebase.database();
if (USE_EMULATORS) {
  // Port 9000 matches firebase.json's existing emulators.database.port
  // (already proven to work in this repo - packages/functions' test:rules
  // script has run the RTDB emulator on this exact port for a separate
  // suite since before this migration).
  firebaseDB.useEmulator('127.0.0.1', 9000);
}

// Performance Monitoring has no emulator, and its SDK beacons to real
// Google endpoints on every call - stubbed rather than initialized under
// the emulator so it can never make a real network call from a test run.
// The stub must cover EVERY PerformanceTrace method the app actually calls
// on the post-login hot path that essentially every e2e spec traverses -
// grep `trace.` under src/: start, stop, putMetric and putAttribute
// (redux-actions/portfoliosList.actions.js and ledgersList.actions.js call
// putMetric on their success path and putAttribute in their catch block).
// Missing either of the latter two is NOT cosmetic under the emulator:
// `trace.putMetric(...)` throws a TypeError inside refreshPortfoliosList's
// own `try`, the catch then throws a SECOND TypeError on
// `trace.putAttribute(...)` which escapes the thunk entirely - so
// PORTFOLIOS_LIST_FINISHED_LOADING (and the ledgers equivalent) is never
// dispatched and the list stays stuck in its loading state forever.
const emulatorPerfStub: EmulatorPerf = {
  trace: () => {
    return {
      start: () => {},
      stop: () => {},
      record: () => {},
      putMetric: () => {},
      getMetric: () => {
        return 0;
      },
      incrementMetric: () => {},
      putAttribute: () => {},
      getAttribute: () => {
        return undefined;
      },
      removeAttribute: () => {},
      getAttributes: () => {
        return {};
      },
    };
  },
};
export const firebasePerf: firebase.performance.Performance | EmulatorPerf =
  USE_EMULATORS ? emulatorPerfStub : firebase.performance();

// Test-only hook for the Playwright e2e suite
// (packages/web/e2e/cross-tab-identity.spec.ts), which needs to authenticate
// a SECOND tab as a different, already-existing user while a first tab in
// the same BrowserContext is still signed in - the exact trigger for
// firebase's cross-tab onAuthStateChanged broadcast that
// reducers/auth.reducer.ts's `isDifferentUser` handling exists to guard
// against. There is no way to do this by driving this app's own UI: once
// any tab in a browser shares an authenticated session, every OTHER tab
// sharing that same origin/storage also inherits it on load (that's the
// real, correct Firebase LOCAL-persistence behavior), and
// components/auth/NonAuthRoute.react.tsx then redirects any already-
// authenticated tab away from /auth/login and /auth/signup before their
// forms ever render - so a second tab can never reach a login/signup form
// again without an explicit prior logout. This hook calls the exact same
// `firebaseAuth.signInWithEmailAndPassword` that
// redux-actions/auth.actions.ts's real `loginUser` thunk calls, so it still
// exercises the real Firebase Auth SDK and its real cross-tab broadcast
// end-to-end - it only bypasses the UI route guard, not any auth logic.
//
// Gate on APP_ENV === 'emulator' (build-time folded via Vite define), NOT
// on NODE_ENV !== 'production'. CI / `pnpm test:e2e:emulator` serves
// `build:emulator` (`vite build --mode build-emulator`), which is
// NODE_ENV=production + APP_ENV=emulator. Gating on NODE_ENV stripped this
// hook from the only bundle the suite runs against and made
// cross-tab-identity.spec.ts fail as soon as smoke e2e actually executed.
// Stage/prod modes set APP_ENV to stage/production, so the branch is still
// eliminated from those bundles.
if (process.env.APP_ENV === 'emulator') {
  window.__myfolioE2E__ = {
    signInAsExistingUser: (email, password) => {
      return firebaseAuth.signInWithEmailAndPassword(email, password);
    },
  };
}

let _store: Store | null = null;

// From https://github.com/btomashvili/react-redux-firebase-boilerplate
const firebaseTools = {
  setStore: (store: Store) => {
    _store = store;
  },

  dispatch: (action: UnknownAction) => {
    return (_store as Store).dispatch(action);
  },

  // Read-only access to the current store state, mirroring `dispatch` above
  // (same `_store` singleton, populated by the same `setStore(store)` call).
  // Needed so fire-and-forget async work kicked off outside a thunk (e.g.
  // auth.actions.js's onAuthStateChanged listener) can check, once it
  // resolves, whether the session it was fetching for is still the current
  // one before dispatching a stale result.
  getState: () => {
    return (_store as Store).getState();
  },

  /**
   * Return the firebase placeholder for the server timestamp
   *
   * @returns {firebase.database.ServerValue.TIMESTAMP}
   */
  timestamp: () => {
    return firebase.database.ServerValue.TIMESTAMP;
  },

  /**
   * Return an instance of a firebase auth provider based on the provider string.
   *
   * @param provider
   * @returns {firebase.auth.AuthProvider}
   */
  getProvider: (provider: AuthProviderName): firebase.auth.AuthProvider => {
    switch (provider) {
      case 'email':
        return new firebase.auth.EmailAuthProvider();
      case 'facebook':
        return new firebase.auth.FacebookAuthProvider();
      case 'github':
        return new firebase.auth.GithubAuthProvider();
      case 'google':
        return new firebase.auth.GoogleAuthProvider();
      case 'twitter':
        return new firebase.auth.TwitterAuthProvider();
      default:
        throw new Error('Provider is not supported!!!');
    }
  },

  /**
   * Login with provider => p is provider "email", "facebook", "github", "google", or "twitter"
   * Uses Popup therefore provider must be an OAuth provider. EmailAuthProvider will throw an error
   *
   * @returns {any|!firebase.Thenable.<*>|firebase.Thenable<any>}
   */
  loginWithProvider: (p: AuthProviderName) => {
    const provider = firebaseTools.getProvider(p);
    return firebaseAuth.signInWithPopup(provider).then(
      () => {
        return firebaseAuth.currentUser;
      },
      (error: unknown): ErrorResult => {
        return { error };
      }
    );
  },

  /**
   * Register a user with email and password
   *
   * @param user
   * @returns {any|!firebase.Thenable.<*>|firebase.Thenable<any>}
   */
  registerUser: (user: EmailPasswordUser) => {
    return (
      firebaseAuth
        .createUserWithEmailAndPassword(user.email, user.password)
        // createUserWithEmailAndPassword resolves a UserCredential, which
        // already has its own `.user` (the real Firebase User with
        // `.uid`/`.email`/`.displayName`). Unwrap it here so this resolves a
        // single, bare-user wrap (`{ user: RealUser }`), consistent with the
        // rest of this file's convention (see loginWithProvider/fetchUser/
        // updateUserProfile) - not `{ user: UserCredential }`, which would put
        // the real user data one level too deep at `result.user.user`.
        .then((userInfo): UserResult => {
          return { user: userInfo.user };
        })
        .catch((error: unknown): ErrorResult => {
          return { error };
        })
    );
  },

  /**
   * Sign the user out
   *
   * @returns {!firebase.Promise.<*>|firebase.Thenable<any>|firebase.Promise<any>|!firebase.Thenable.<*>}
   */
  logoutUser: () => {
    return firebaseAuth.signOut().then((): LogoutResult => {
      return {
        success: 1,
        message: 'logout',
      };
    });
  },

  /**
   * Retrieve the current user (Promise)
   * @returns {Promise}
   */
  fetchUser: () => {
    return new Promise<firebase.User | null>((resolve, reject) => {
      const unsub = firebaseAuth.onAuthStateChanged(
        (user) => {
          unsub();
          resolve(user);
        },
        (error) => {
          reject(error);
        }
      );
    });
  },

  /**
   * Log the user in using email and password
   *
   * @param user
   * @returns {any|!firebase.Thenable.<*>|firebase.Thenable<any>}
   */
  loginUser: (user: EmailPasswordUser) => {
    return (
      firebaseAuth
        .signInWithEmailAndPassword(user.email, user.password)
        // signInWithEmailAndPassword resolves a UserCredential, which already
        // has its own `.user`. Unwrap it here - see the identical comment on
        // registerUser above.
        .then((userInfo): UserResult => {
          return { user: userInfo.user };
        })
        .catch((error: unknown): ErrorResult => {
          return { error };
        })
    );
  },

  /**
   * Update a user's profile data
   *
   * @param u
   * @returns {!firebase.Promise.<*>|firebase.Thenable<any>|firebase.Promise<any>|!firebase.Thenable.<*>}
   */
  updateUserProfile: (u: ProfileUpdate) => {
    return firebaseAuth.currentUser!.updateProfile(u).then(
      () => {
        return firebaseAuth.currentUser;
      },
      (error: unknown): ErrorResult => {
        return { error };
      }
    );
  },

  /**
   * Reset the password given the specified email
   *
   * @param email {string}
   * @returns {!firebase.Promise.<*>|firebase.Thenable<any>|firebase.Promise<any>|!firebase.Thenable.<*>}
   */
  resetPasswordEmail: (email: string) => {
    return firebaseAuth.sendPasswordResetEmail(email).then(
      (): MessageResult => {
        return {
          message: 'Email sent',
        };
      },
      (error: unknown): ErrorResult => {
        return { error };
      }
    );
  },

  /**
   * Update the user's password with the given password
   *
   * @param newPassword {string}
   * @returns {!firebase.Promise.<*>|firebase.Thenable<any>|firebase.Promise<any>|!firebase.Thenable.<*>}
   */
  changePassword: (newPassword: string) => {
    return firebaseAuth.currentUser!.updatePassword(newPassword).then(
      () => {
        return undefined;
      },
      (error: unknown): ErrorResult => {
        return { error };
      }
    );
  },

  /**
   * Send an account email verification message for the currently logged in user
   *
   * @returns {!firebase.Promise.<*>|firebase.Thenable<any>|firebase.Promise<any>|!firebase.Thenable.<*>}
   */
  sendEmailVerification: () => {
    return firebaseAuth.currentUser!.sendEmailVerification().then(
      (): MessageResult => {
        return {
          message: 'Email sent',
        };
      },
      (error: unknown): ErrorResult => {
        return { error };
      }
    );
  },

  /**
   * Get the firebase database reference.
   *
   * @param path {!string|string}
   * @returns {!firebase.database.Reference|firebase.database.Reference}
   */
  getDatabaseReference: (path: string) => {
    return firebaseDB.ref(path);
  },
};

export type FirebaseTools = typeof firebaseTools;

export default firebaseTools;
